/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import AutocompleteDropDown, { IOption } from 'components/AutocompleteDropDown/AutocompleteDropDown';
import { FormikProps } from 'formik';
import { components } from 'generated/payment-initialisation/schema';
import { TCountries } from 'models/countries';
import t from 'utils/translationHelper';

import { CurrencyList } from '@alpha/bene-dtos';
import {
  FormControlLabel, Radio, RadioGroup,
} from '@alpha/ui-lib/ui/external';
import { Input } from '@alpha/ui-lib/ui/Input/Input';
import { APTooltip } from '@alpha/ui-lib/ui/Tooltip';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { IRemitterFormValues } from './RemitterDrawer';
import useStyles from './RemitterDrawer.styles';

interface IBasicInformationProps {
  form: FormikProps<IRemitterFormValues>;
  countries: TCountries;
  currencies: CurrencyList;
  editRemitterValues?: components['schemas']['RemitterResponse'];
}

const BasicInformation: React.FC<IBasicInformationProps> = ({
  form, countries, currencies, editRemitterValues,
}) => {
  const styles = useStyles();
  const [countryOption, setCountryOption] = useState<IOption>({ name: countries.find((country) => country.code === form.values.country)?.name, code: form.values.country });
  const [countryTextFieldValue, setCountryTextFieldValue] = useState(countries.find((country) => country.code === form.values.country)?.name);
  const [currencyOption, setCurrencyOption] = useState<IOption>({ name: currencies.currencies.find((currency) => currency.code === form.values.currency)?.name, code: form.values.currency });
  const [currencyTextFieldValue, setCurrencyTextFieldValue] = useState(currencies.currencies.find((currency) => currency.code === form.values.currency)?.name);

  const handlePartyTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    form.setFieldValue('partyType', Number(event.target.value) as 1 | 3);
  };

  return (
    <div style={{
      border: '1px solid #E4E4E7', borderRadius: '8px', padding: '16px', marginTop: '24px',
    }}
    >
      <div style={{ fontWeight: 600, marginBottom: '16px' }}>{t('basic_information')}</div>
      <div style={{ marginBottom: '6px' }}>
        <Typography
          variant="subtitle1"
          style={{
            marginBottom: '6px',
            color: editRemitterValues?.Fields?.partyType?.Editable
                 && (!form.values.partyType) ? '#CF752F' : 'unset',
          }}
        >
          {(editRemitterValues?.Fields?.partyType?.Editable
                 && (!form.values.partyType))
            ? (<FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#CF752F', marginRight: '4px' }} />) : null}
          {t('is_this_remitter_1st_or_3rd_party')}
          *
        </Typography>
        <div>
          {(() => {
            const isNotEditable = editRemitterValues?.Fields?.partyType?.Editable === false;
            const radioGroupElement = (
              <RadioGroup
                row
                value={form.values.partyType}
                onChange={handlePartyTypeChange}
              >
                <FormControlLabel
                  value={1}
                  control={<Radio color="primary" />}
                  label={t('1party')}
                  data-testid="remitter-first-party"
                  disabled={editRemitterValues?.Fields?.ThirdParty?.Editable === false}
                />
                <FormControlLabel
                  value={3}
                  control={<Radio color="primary" />}
                  label={t('3party')}
                  data-testid="remitter-third-party"
                  disabled={editRemitterValues?.Fields?.ThirdParty?.Editable === false}
                />
              </RadioGroup>
            );

            return isNotEditable ? (
              <APTooltip title={t('you_are_unable_to_edit_this_information')} arrow>
                <div>{radioGroupElement}</div>
              </APTooltip>
            ) : (
              radioGroupElement
            );
          })()}
        </div>
      </div>
      <div style={{ marginBottom: '16px' }}>
        <Typography
          variant="subtitle1"
          style={{
            marginBottom: '6px',
            color: editRemitterValues?.Fields?.Payee?.Editable
                 && (!form.values.remitterName) ? '#CF752F' : 'unset',
          }}
        >
          {(editRemitterValues?.Fields?.Payee?.Editable
                 && (!form.values.remitterName))
            ? (<FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#CF752F', marginRight: '4px' }} />) : null}
          {t('remitter_name')}
          *
        </Typography>
        {(() => {
          const isNotEditable = editRemitterValues?.Fields?.Payee?.Editable === false;
          const inputElement = (
            <Input
              fullWidth
              variant="filled"
              placeholder={t('enter_remitter_name')}
              testId="remitter-name"
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              value={form.values.remitterName}
              name="remitterName"
              type="text"
              className={styles.textInput}
              error={form.touched.remitterName && Boolean(form.errors.remitterName)}
              helperText={form.touched.remitterName && form.errors.remitterName}
              disabled={editRemitterValues?.Fields?.Payee?.Editable === false}
            />
          );

          return isNotEditable ? (
            <APTooltip title={t('you_are_unable_to_edit_this_information')} arrow>
              <div>{inputElement}</div>
            </APTooltip>
          ) : (
            inputElement
          );
        })()}
      </div>
      <div style={{ marginBottom: '16px' }}>
        <Typography
          variant="subtitle1"
          style={{
            marginBottom: '6px',
            color: editRemitterValues?.Fields?.PayeeCountryCode?.Editable
                 && (!form.values.country) ? '#CF752F' : 'unset',
          }}
        >
          {(editRemitterValues?.Fields?.PayeeCountryCode?.Editable
                 && (!form.values.country))
            ? (<FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#CF752F', marginRight: '4px' }} />) : null}
          {t('country')}
          *
        </Typography>

        {(() => {
          const isNotEditable = editRemitterValues?.Fields?.PayeeCountryCode?.Editable === false;
          const inputElement = (
            <AutocompleteDropDown
              name="country"
              testId="country-dropdown"
              options={countries}
              setFieldValue={form.setFieldValue}
              value={form.values.country}
              touched={form.touched.country}
              errors={form.errors?.country}
              currentOption={countryOption}
              setCurrentOption={setCountryOption}
              textFieldValue={countryTextFieldValue}
              setTextFieldValue={setCountryTextFieldValue}
              disabled={editRemitterValues?.Fields?.PayeeCountryCode?.Editable === false}
            />
          );
          return isNotEditable ? (
            <APTooltip title={t('you_are_unable_to_edit_this_information')} arrow>
              <div>{inputElement}</div>
            </APTooltip>
          ) : (
            inputElement
          );
        })()}
      </div>
      <div style={{ marginBottom: '16px' }}>
        <Typography
          variant="subtitle1"
          style={{
            marginBottom: '6px',
            color: editRemitterValues?.Fields?.Currency?.Editable
                 && (!form.values.currency) ? '#CF752F' : 'unset',
          }}
        >
          {(editRemitterValues?.Fields?.Currency?.Editable
                 && (!form.values.currency))
            ? (<FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#CF752F', marginRight: '4px' }} />) : null}
          {t('currency')}
          *
        </Typography>
        {(() => {
          const isNotEditable = editRemitterValues?.Fields?.Currency?.Editable === false;
          const inputElement = (
            <AutocompleteDropDown
              name="currency"
              testId="currency-dropdown"
              options={currencies.currencies.map((currency) => ({ name: currency.name, code: currency.code }))}
              setFieldValue={form.setFieldValue}
              value={form.values.currency}
              touched={form.touched.currency}
              errors={form.errors?.currency}
              currentOption={currencyOption}
              setCurrentOption={setCurrencyOption}
              textFieldValue={currencyTextFieldValue}
              setTextFieldValue={setCurrencyTextFieldValue}
              disabled={editRemitterValues?.Fields?.Currency?.Editable === false}
            />
          );

          return isNotEditable ? (
            <APTooltip title={t('you_are_unable_to_edit_this_information')} arrow>
              <div>{inputElement}</div>
            </APTooltip>
          ) : (
            inputElement
          );
        })()}
      </div>
    </div>
  );
};

export default BasicInformation;
